import { useState, useCallback, useEffect, useRef } from 'react'
import { Header, Footer, useTheme } from '@core-loop/ui'
import '@core-loop/ui/tokens.css'
import '@core-loop/ui/header.css'
import '@core-loop/ui/footer.css'
import { useAuth } from '@core-loop/auth-client'
import { FinanceCopyContext } from './CopyContext'
import { FINANCE_COPY } from './locales'
import { setTelemetryUser } from './telemetry'
import { TopCard } from './components/TopCard'
import { FinanceChatEmbed } from './components/FinanceChatEmbed'
import { EventsPanel } from './components/EventsPanel'
import { NetWorthChart } from './components/NetWorthChart'
import { CashFlowChart } from './components/CashFlowChart'
import { ScenarioSummary } from './components/ScenarioSummary'
import { RealityTracker } from './components/RealityTracker'
import { runSimulation, defaultParams } from './engine/montecarlo'
import { loadSavedProfile, saveProfile, clearSavedProfile, loadRememberMe, saveRememberMe } from './utils/storage'
import { exportToJSON, importFromJSON, generateShareURL, readShareFromURL, applySharePayload, clearShareFragment } from './utils/sharing'
import type { SimulationParams, SimulationResult, RealityEntry } from './engine/types'
import './index.css'

function initialParams(): SimulationParams {
  const base = defaultParams()
  const saved = loadSavedProfile()
  if (saved) return { ...base, profile: saved }
  return base
}

export function App() {
  const [params, setParams] = useState<SimulationParams>(initialParams)
  const [result, setResult] = useState<SimulationResult | null>(null)
  const [running, setRunning] = useState(false)
  const [realityEntries, setRealityEntries] = useState<RealityEntry[]>([])
  const [activeTab, setActiveTab] = useState<'simulation' | 'reality'>('simulation')
  const [activeScenario, setActiveScenario] = useState<string | null>(null)
  const [rememberMe, setRememberMe] = useState(() => loadRememberMe())
  const [shareToast, setShareToast] = useState<string | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [loadedFromShare, setLoadedFromShare] = useState(false)
  const [mobilePanel, setMobilePanel] = useState<'chat' | 'sim' | 'events'>('sim')
  const autoRunPending = useRef(false)

  // On mount: read share URL and restore params if present
  useEffect(() => {
    const payload = readShareFromURL()
    if (payload) {
      const base = defaultParams()
      const birthYear = payload.p.birthYear || 1985
      const partnerBirthYear = payload.p.partnerBirthYear
      const youngestBirthYear = partnerBirthYear > 0 ? Math.max(birthYear, partnerBirthYear) : birthYear
      const projYears = (youngestBirthYear + 100) - base.startYear
      const userSS = { ...base.userSS, birthYear }
      const spouseSS = partnerBirthYear > 0 ? { ...base.spouseSS, birthYear: partnerBirthYear } : base.spouseSS
      const restored = applySharePayload(base, payload)
      const finalParams = { ...restored, projectionYears: Math.max(projYears, 10), userSS, spouseSS }
      setParams(finalParams)
      setActiveScenario('Shared Scenario')
      setLoadedFromShare(true)
      clearShareFragment()
      autoRunPending.current = true
    }
  }, []) // mount-only — intentionally captures no deps; payload applied via direct setters

  // Auto-run after share URL restore: fires whenever params changes while flag is set
  useEffect(() => {
    if (!autoRunPending.current) return
    autoRunPending.current = false
    setRunning(true)
    setTimeout(() => {
      try {
        setResult(runSimulation(params))
      } finally {
        setRunning(false)
      }
    }, 10)
  }, [params]) // runs after params state update commits

  function handleRememberMeChange(value: boolean) {
    setRememberMe(value)
    saveRememberMe(value)
    // If user just unchecked, immediately clear any saved data
    if (!value) clearSavedProfile()
  }

  // projectionYears = youngest person lives to 100
  // "youngest" = most recent birth year among user and partner (if set)
  function handleParamsChange(updated: SimulationParams) {
    const birthYear = updated.profile.birthYear || 1985
    const partnerBirthYear = updated.profile.partnerBirthYear
    const youngestBirthYear = partnerBirthYear > 0
      ? Math.max(birthYear, partnerBirthYear)
      : birthYear
    const projYears = (youngestBirthYear + 100) - updated.startYear
    // Sync SS birthYear from profile so SS calc stays consistent
    const userSS = { ...updated.userSS, birthYear }
    const spouseSS = partnerBirthYear > 0
      ? { ...updated.spouseSS, birthYear: partnerBirthYear }
      : updated.spouseSS
    // Auto-set retirement target to 25x expenses when not yet set
    const totalExpenses = updated.profile.annualHousingExpense + updated.profile.annualOtherExpenses
    const suggestedTarget = totalExpenses > 0 ? Math.round(totalExpenses * 25 / 100_000) * 100_000 : 0
    const retirementTargetNetWorth = updated.retirementTargetNetWorth === 0 && suggestedTarget > 0
      ? suggestedTarget
      : updated.retirementTargetNetWorth
    setParams({ ...updated, projectionYears: Math.max(projYears, 10), userSS, spouseSS, retirementTargetNetWorth })
  }

  function handleExport() {
    exportToJSON(params)
  }

  async function handleImport(file: File) {
    setImportError(null)
    try {
      const imported = await importFromJSON(file)
      handleParamsChange(imported)
      setActiveScenario('Imported Scenario')
    } catch (err) {
      const isDebug = import.meta.env.DEV || new URLSearchParams(window.location.search).get('debug') === 'true'
      setImportError(isDebug && err instanceof Error ? err.message : 'Invalid or corrupted file. Make sure you\'re importing a 312-finance export (.json).')
      setTimeout(() => setImportError(null), 5000)
    }
  }

  function handleShare() {
    const url = generateShareURL(params)
    navigator.clipboard.writeText(url).then(() => {
      setShareToast('✓ Copied!')
      setTimeout(() => setShareToast(null), 2500)
    }).catch(() => {
      // Clipboard blocked — show the URL for manual copy
      setShareToast('URL ready')
      setTimeout(() => setShareToast(null), 2500)
    })
  }

  const handleRun = useCallback(() => {
    // Persist profile to localStorage if user opted in
    if (rememberMe) {
      saveProfile(params.profile)
    } else {
      clearSavedProfile()
    }
    setRunning(true)
    // Defer to allow React to update the button state before heavy computation
    setTimeout(() => {
      try {
        const res = runSimulation(params)
        setResult(res)
      } finally {
        setRunning(false)
      }
    }, 10)
  }, [params, rememberMe])

  // Derive actual net worth points for reality overlay on charts
  const { theme, toggleTheme } = useTheme()
  const { user, token: _authToken, isLoading: authLoading, login, logout } = useAuth()

  // HC-TELEMETRY-IDENTITY: link opaque user ID after sign-in
  useEffect(() => {
    setTelemetryUser(user?.id ?? null)
  }, [user])

  // ── Login modal state ──────────────────────────────────────────────────────
  const AUTH_URL = String(import.meta.env.VITE_AUTH_URL || 'https://auth.core-loop.dev').replace(/\/$/, '')

  // LLM key fetch removed -- finance chat now uses ChatEmbed which routes
  // through chat.core-loop.dev with its own auth. No client-side key needed.

  const [loginOpen, setLoginOpen] = useState(false)
  const [loginForm, setLoginForm] = useState({ email: '', password: '', confirmPassword: '' })
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginMode, setLoginMode] = useState<'login' | 'register'>('login')
  // Track whether the mousedown that triggered a click started on the overlay itself
  // (not on a child). Prevents dismissal when user drags text selection outside the modal.
  const overlayMouseDownRef = useRef(false)

  useEffect(() => {
    if (!loginOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setLoginOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [loginOpen])

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    if (loginMode === 'register' && loginForm.password !== loginForm.confirmPassword) {
      setLoginError('Passwords do not match.')
      return
    }
    setLoginLoading(true)
    try {
      const endpoint = loginMode === 'register' ? 'auth/register' : 'auth/login'
      const res = await fetch(`${AUTH_URL}/api/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginForm.email, password: loginForm.password }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setLoginError(data.message || 'Authentication failed.')
        return
      }
      login(data.accessToken)
      setLoginOpen(false)
      setLoginForm({ email: '', password: '', confirmPassword: '' })
    } catch {
      setLoginError('Network error. Please try again.')
    } finally {
      setLoginLoading(false)
    }
  }

  const realityChartData = realityEntries.map(e => ({
    year: e.calendarYear,
    netWorth:
      e.liquidAssets + e.retirementAssets + e.homeEquity,
  }))

  const copy = FINANCE_COPY

  // Language picker -- Finance app is EN-only; non-EN selections open the Blog in that locale
  // so the user gets localised content without leaving the Finance app unexpectedly.
  const SUPPORTED_LOCALES = ['en','es','de','ja','fr','pt','ru','uk','cs','it','nl','pl','tr','zh-CN']
  const [langOpen, setLangOpen] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!langOpen) return
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [langOpen])

  const langPicker = (
    <div className="fin-lang-pick" ref={langRef}>
      <button
        type="button"
        className="fin-lang-btn"
        aria-haspopup="listbox"
        aria-expanded={langOpen}
        aria-label="Select language"
        onClick={() => setLangOpen(o => !o)}
      >
        EN ▾
      </button>
      {langOpen && (
        <ul className="fin-lang-menu" role="listbox" aria-label="Language">
          {SUPPORTED_LOCALES.map(l => (
            <li key={l} role="option" aria-selected={l === 'en'}>
              <a
                className={`fin-lang-option${l === 'en' ? ' fin-lang-option--active' : ''}`}
                href={l === 'en' ? 'https://finance.core-loop.dev' : `https://blog.core-loop.dev/${l}`}
                title={l !== 'en' ? 'Finance is English-only — opens the Blog in selected language' : undefined}
              >
                {l}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  return (
    <FinanceCopyContext.Provider value={copy}>
    <div className="app-shell" data-theme={theme}>
      {/* ── Login modal ── */}
      {loginOpen && (
        <div
          className="auth-modal__overlay"
          role="dialog"
          aria-label={loginMode === 'register' ? 'Create account' : 'Sign in'}
          onMouseDown={(e) => { overlayMouseDownRef.current = e.target === e.currentTarget }}
          onClick={(e) => { if (overlayMouseDownRef.current && e.target === e.currentTarget) setLoginOpen(false) }}
        >
          <div className="auth-modal">
            <button type="button" className="auth-modal__close" onClick={() => setLoginOpen(false)} aria-label="Close">✕</button>
            <h2 className="auth-modal__title">{loginMode === 'register' ? 'Create account' : 'Sign in'}</h2>
            <form onSubmit={handleLoginSubmit} className="auth-modal__form">
              <input
                type="email"
                className="auth-modal__input"
                placeholder="Email"
                value={loginForm.email}
                onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
                required
                autoFocus
              />
              <input
                type="password"
                className="auth-modal__input"
                placeholder="Password"
                value={loginForm.password}
                onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                required
                minLength={loginMode === 'register' ? 8 : 1}
              />
              {loginMode === 'register' && (
                <input
                  type="password"
                  className="auth-modal__input"
                  placeholder="Confirm password"
                  value={loginForm.confirmPassword}
                  onChange={e => setLoginForm(f => ({ ...f, confirmPassword: e.target.value }))}
                  required
                  minLength={8}
                />
              )}
              {loginError && <p className="auth-modal__error" role="alert">{loginError}</p>}
              <button type="submit" className="auth-modal__submit" disabled={loginLoading}>
                {loginLoading ? '...' : loginMode === 'register' ? 'Create account' : 'Sign in'}
              </button>
            </form>
            <button
              type="button"
              className="auth-modal__toggle"
              onClick={() => { setLoginMode(loginMode === 'login' ? 'register' : 'login'); setLoginError(''); setLoginForm(f => ({ ...f, confirmPassword: '' })) }}
            >
              {loginMode === 'login' ? 'No account? Create one' : 'Have an account? Sign in'}
            </button>
          </div>
        </div>
      )}
      <Header
        appName="Finance"
        appHref="https://finance.core-loop.dev"
        theme={theme}
        onToggleTheme={toggleTheme}
        user={!authLoading && user ? { email: user.email, roles: user.roles } : null}
        onLogin={() => { setLoginOpen(true); setLoginMode('login'); setLoginError('') }}
        onLogout={logout}
        hideAuth={false}
        extraActions={langPicker}
        authUrl={AUTH_URL}
      />
      <nav className="fin-subnav" aria-label="Finance sections">
        <button
          className={activeTab === 'simulation' ? 'fin-tab fin-tab--active' : 'fin-tab'}
          onClick={() => setActiveTab('simulation')}
        >
          {copy.tabSimulation}
        </button>
        <button
          className={activeTab === 'reality' ? 'fin-tab fin-tab--active' : 'fin-tab'}
          onClick={() => setActiveTab('reality')}
        >
          {copy.tabRealityTracker}
        </button>
        <span className="fin-subnav__tagline">{copy.tagline}</span>
      </nav>

      {activeTab === 'simulation' && (
        <>
          <TopCard
            params={params}
            onChange={handleParamsChange}
            onRun={handleRun}
            running={running}
            rememberMe={rememberMe}
            onRememberMeChange={handleRememberMeChange}
            onExport={handleExport}
            onImport={handleImport}
            onShare={handleShare}
            shareToast={shareToast}
            loadedFromShare={loadedFromShare}
          />

          <div className="sim-layout">
            <FinanceChatEmbed
              activeScenario={activeScenario}
              hidden={mobilePanel !== 'chat'}
              isAuthenticated={!authLoading && !!user}
            />

            <main className={`sim-main${mobilePanel !== 'sim' ? ' sim-main--mobile-hidden' : ''}`}>
              {importError && (
                <div className="import-error-banner">⚠ {importError}</div>
              )}
              {activeScenario && (
                <div className="scenario-indicator">
                  <span className="scenario-indicator-dot" />
                  <strong>{copy.scenarioLabel}</strong> {activeScenario}
                  <button className="scenario-indicator-clear" onClick={() => setActiveScenario(null)}>✕</button>
                </div>
              )}
              {!result && !running && (
                <div className="empty-state">
                  <p>{copy.emptyStatePrompt}</p>
                </div>
              )}
              {running && (
                <div className="empty-state">
                  <p>{copy.runningState(params.iterations)}</p>
                </div>
              )}
              {result && !running && (
                <>
                  <ScenarioSummary result={result} startYear={params.startYear} retirementTarget={params.retirementTargetNetWorth} inflationMean={params.inflation.mean} />
                  <NetWorthChart result={result} retirementTarget={params.retirementTargetNetWorth} realityData={realityChartData} inflationMean={params.inflation.mean} startYear={params.startYear} />
                  <CashFlowChart result={result} inflationMean={params.inflation.mean} startYear={params.startYear} />
                </>
              )}
            </main>

            <EventsPanel
              events={params.events ?? []}
              onChange={events => handleParamsChange({ ...params, events })}
              startYear={params.startYear}
              profile={params.profile}
              params={params}
              onParamsChange={handleParamsChange}
              onRunSimulation={handleRun}
              onScenarioChange={setActiveScenario}
              hidden={mobilePanel !== 'events'}
            />
          </div>

          <nav className="mobile-panel-nav" aria-label="Panel navigation">
            <button
              className={`mobile-panel-btn${mobilePanel === 'chat' ? ' mobile-panel-btn--active' : ''}`}
              onClick={() => setMobilePanel('chat')}
            >
              <span aria-hidden="true">💬</span> {copy.mobileAI}
            </button>
            <button
              className={`mobile-panel-btn${mobilePanel === 'sim' ? ' mobile-panel-btn--active' : ''}`}
              onClick={() => setMobilePanel('sim')}
            >
              <span aria-hidden="true">📊</span> {copy.mobileCharts}
            </button>
            <button
              className={`mobile-panel-btn${mobilePanel === 'events' ? ' mobile-panel-btn--active' : ''}`}
              onClick={() => setMobilePanel('events')}
            >
              <span aria-hidden="true">📅</span> {copy.mobileEvents}
            </button>
          </nav>
        </>
      )}

      {activeTab === 'reality' && (
        <div className="reality-layout">
          <RealityTracker onDataChange={setRealityEntries} />
          {result && realityEntries.length > 0 && (
            <NetWorthChart
              result={result}
              retirementTarget={params.retirementTargetNetWorth}
              realityData={realityChartData}
              inflationMean={params.inflation.mean}
              startYear={params.startYear}
            />
          )}
          {(!result || realityEntries.length === 0) && (
            <p className="reality-hint">
              {!result
                ? copy.realityHintNoSim
                : copy.realityHintNoEntries}
            </p>
          )}
        </div>
      )}
    </div>
    <Footer
      appName="Finance"
      theme={theme}
      onToggleTheme={toggleTheme}
    />
    </FinanceCopyContext.Provider>
  )
}
